import { computed, inject, readonly, ref, watch } from 'vue';
import { agentsFilters } from '@/services/agentsFiltersGQLAdapter';
import { IKAgentsDrawerFilter } from '@/types/injectionKeys';
import { app } from '@/bootloader';
import { ArgumentTypes } from '@/types/utils';
import { IKAgentsPersistentData } from '@/views/agents/agents.injectionKeys';
import { useOsService } from '@/services/OsService/OsService';
import { IKApplicationPersistentData } from '@/services/persistentDataProvider/persistentDataProvider.types';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { useAgentsFiltersMetadataLazyQuery } from '@/graphql/codegen/generated/graphql';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import _ from 'lodash';
import { AgentsFilter, AgentsFilterRef, mapFilterModelToGQLFilter } from './AgentTableFilters.types';

export const useAgentTableFiltersProvider = () => {
  const osService = useOsService();
  const coordinatorsService = inject(IKCoordinatorsService);
  const GQL_AgentsFilter = ref<AgentsFilterRef>({});
  const agentsPagesPersistentData = inject(IKAgentsPersistentData);
  const appPersistentData = inject(IKApplicationPersistentData);

  const defaultFilterModel = {
    windows: {
      dirty: {},
      activeFiltersCount: 0,
      licensesFilter: {
        local: true,
        initiator: true,
        ciInitiator: true,
        helper: true,
        cloudAgent: true,
        bothHelperAndInitiator: true,
        bothHelperAndCIInitiator: true,
        initiatorType: {
          value: 'All',
          withBuildCache: 'All',
        },
        ciInitiatorType: {
          value: 'All',
          withBuildCache: 'All',
        },
        helperType: 'All',
      },
      quickFilter: {
        showAll: true,
        active: false,
        inProcess: false,
        agentsIdFilter: false,
        incompatibleVersion: false,
        unlicensedAgents: false,
      },
      buildGroups: {},
      osVersionFilter: {
        windows: true,
        linux: true,
      },
      status: {
        ready: true,
        initiating: true,
        offline: true,
        helping: true,
        updating: true,
        stopped: true,
      },
      cpuView: {
        min: 0,
        max: 100,
      },
      coreView: {
        existingFilters: [],
        enabledFilters: [],
      },
      logLevel: {
        Minimal: true,
        Basic: true,
        Intermediate: true,
        Extended: true,
        Detailed: true,
      },
      cacheView: {
        min: 0,
        max: 0,
        limits: {
          min: 0,
          max: 0,
        },
      },
      helpers: {
        enabled: true,
        disabled: true,
      },
      agentHelperPermissions: {
        allowed: true,
        notAllowed: true,
      },
    },
    linux: {
      dirty: {},
      activeFiltersCount: 0,
      quickFilter: {
        showAll: true,
        active: false,
        inProcess: false,
        agentsIdFilter: false,
        incompatibleVersion: false,
        unlicensedAgents: false,
      },
      licensesFilter: {
        initiator: true,
        helper: true,
        cloudAgent: true,
        bothHelperAndInitiator: true,
      },
      buildGroups: {},
      status: {
        ready: true,
        offline: true,
        stopped: true,
        expired: true,
      },
      cpuView: {
        min: 0,
        max: 100,
      },
    },
  };

  const currentFilterModel = ref({
    windows: _.cloneDeep(defaultFilterModel.windows),
    linux: _.cloneDeep(defaultFilterModel.linux),
  });

  const containsRelevantFilters = (filter: AgentsFilterRef) =>
    Object.keys(filter).some(key => {
      const value = filter[key as keyof AgentsFilterRef];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== undefined && value !== null;
    });

  const filterState = computed({
    get: () => (osService.osType.value === OsType.Windows ? currentFilterModel.value.windows : currentFilterModel.value.linux),
    set: newValue => {
      if (osService.osType.value === OsType.Windows) {
        currentFilterModel.value.windows = newValue as any;
      }
      if (osService.osType.value === OsType.Linux) {
        currentFilterModel.value.linux = newValue as any;
      }
    },
  });

  const clearWindowsFilters = () => {
    const filterInitialize = _.cloneDeep(defaultFilterModel.windows);
    filterInitialize.quickFilter = currentFilterModel.value.windows.quickFilter;
    const { limits: curLimits } = currentFilterModel.value.windows.cacheView;
    const { existingFilters: curExistingFilters } = currentFilterModel.value.windows.coreView;
    currentFilterModel.value.windows = {
      ...filterInitialize,
      buildGroups: Object.keys(currentFilterModel.value.windows.buildGroups).reduce((buildGroups, key) => ({ ...buildGroups, [key]: true }), {}),
      cacheView: { ...filterInitialize.cacheView, limits: curLimits, max: curLimits.max },
      coreView: { ...filterInitialize.coreView, existingFilters: curExistingFilters, enabledFilters: [...curExistingFilters] },
    };
  };

  const clearLinuxFilters = () => {
    const filterInitialize = _.cloneDeep(defaultFilterModel.linux);
    filterInitialize.quickFilter = currentFilterModel.value.linux.quickFilter;
    currentFilterModel.value.linux = {
      ...filterInitialize,
      buildGroups: Object.keys(currentFilterModel.value.linux.buildGroups).reduce((buildGroups, key) => ({ ...buildGroups, [key]: true }), {}),
    };
  };

  const clearFilters = () => {
    if (osService.osType.value === OsType.Windows) {
      clearWindowsFilters();
    } else {
      clearLinuxFilters();
    }
  };

  const setAgentsCoreView = (agentCores: any) => {
    const agentsCoresSet = new Set(agentCores);
    const currentEnabled = new Set((currentFilterModel.value.windows as any).coreView?.enabledFilters);
    const currentExisting = new Set((currentFilterModel.value.windows as any).coreView.existingFilters);
    if (!(currentFilterModel.value.windows as any).dirty?.coreView && !(currentFilterModel.value.windows as any).coreView?.enabledFilters?.length) {
      (currentFilterModel.value.windows as any).coreView.existingFilters = agentCores;
      (currentFilterModel.value.windows as any).coreView.enabledFilters = (currentFilterModel.value.windows as any).coreView.existingFilters.slice(0);
    } else {
      const coresDiff = Array.from(new Set([...(currentFilterModel.value.windows as any).coreView.existingFilters, ...agentCores])).reduce(
        (prev, cores) => {
          if (!currentExisting.has(cores)) return { ...prev, newAddedCores: new Set([...prev.newAddedCores, cores]) };
          if (currentExisting.has(cores) && !agentsCoresSet.has(cores))
            return {
              ...prev,
              removedCores: new Set([...prev.removedCores, cores]),
            };
          return prev;
        },
        {
          removedCores: new Set(),
          newAddedCores: new Set(),
        },
      );

      coresDiff.removedCores.forEach((core: any) => {
        currentEnabled.delete(core);
      });
      coresDiff.newAddedCores.forEach((core: any) => {
        currentEnabled.add(core);
      });
      (currentFilterModel.value.windows as any).coreView.enabledFilters = Array.from(currentEnabled);
      (currentFilterModel.value.windows as any).coreView.existingFilters = agentCores;
    }
  };

  const setAgentCacheView = (data: any) => {
    if (
      (currentFilterModel.value.windows as any).cacheView.max === 0 ||
      ((currentFilterModel.value.windows as any).cacheView.limits.max !== data &&
        (currentFilterModel.value.windows as any).cacheView.max === (currentFilterModel.value.windows as any).cacheView.limits.max)
    ) {
      (currentFilterModel.value.windows as any).cacheView.max = data;
    }
    (currentFilterModel.value.windows as any).cacheView.limits.max = data;
  };

  const setEnabledCoreFilters = (data: any) => {
    (currentFilterModel.value.windows as any).coreView.enabledFilters = data;
    (currentFilterModel.value.windows as any).dirty.coreView = true;
  };

  const setSingleFilter = (data: any) => {
    if (data.filter) {
      if (osService.osType.value === OsType.Windows) {
        (currentFilterModel.value.windows as any)[data.group][data.filter] = data.value;
      } else {
        (currentFilterModel.value.linux as any)[data.group][data.filter] = data.value;
      }
    } else if (data.group) {
      if (osService.osType.value === OsType.Windows) {
        (currentFilterModel.value.windows as any)[data.group] = data.value;
      } else {
        (currentFilterModel.value.linux as any)[data.group] = data.value;
      }
    }
  };

  const updateFilter = (data: any) => {
    if (Array.isArray(data)) {
      data.forEach(item => {
        setSingleFilter(item);
        if (osService.osType.value === OsType.Windows) {
          (currentFilterModel.value.windows as any).dirty[item.group] = true as any;
        } else {
          (currentFilterModel.value.linux as any).dirty[item.group] = true;
        }
      });
    } else {
      setSingleFilter(data);
      if (osService.osType.value === OsType.Windows) {
        (currentFilterModel.value.windows as any).dirty[data.group] = true;
      } else {
        (currentFilterModel.value.linux as any).dirty[data.group] = true;
      }
    }
    agentsPagesPersistentData?.setFilter(filterState.value as any);
  };

  const agentsLeftDrawerActiveFilters = computed(() => filterState.value.activeFiltersCount);

  const setGQL_AgentsFilter = (type: keyof typeof GQL_AgentsFilter.value) => {
    switch (type) {
      case 'coresFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setCoresFilter(filterState.value);
        break;
      case 'availableCpuFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setAvailableCpuFilter(filterState.value);
        break;
      case 'helperPermissionsFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setHelperPermissionsFilter(filterState.value);
        break;
      case 'enabledAsHelperFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setEnabledAsHelperFilter(filterState.value);
        break;
      case 'cacheSizeFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setCacheSizeFilter(filterState.value);
        break;
      case 'logLevelFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setLogLevelFilter(filterState.value);
        break;
      case 'buildGroupsFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setBuildGroupFilter(filterState.value);
        break;
      case 'osVersionFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setOsVersionFilter(filterState.value);
        break;
      case 'statusFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setStatusFilter(filterState.value);
        break;
      case 'licenseFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setLicenseFilter(filterState.value);
        break;
      default:
        break;
    }
  };

  const setGQL_LinuxAgentsFilter = (type: keyof typeof GQL_AgentsFilter.value) => {
    switch (type) {
      case 'buildGroupsFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setBuildGroupFilter(filterState.value);
        break;
      case 'usedCpuFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setUsedCpuFilter(filterState.value);
        break;
      case 'statusFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setStatusFilter(filterState.value);
        break;
      case 'licenseFilter':
        GQL_AgentsFilter.value[type] = agentsFilters.setLicenseFilter(filterState.value);
        break;
      default:
        break;
    }
  };

  const setGQL_AgentsQuickFilter = (payload: ArgumentTypes<typeof agentsFilters.setAgentsQuickFilter>[0]) => {
    delete GQL_AgentsFilter.value.agentsIdFilter;
    GQL_AgentsFilter.value.quickFilter = agentsFilters.setAgentsQuickFilter(payload);
  };

  const setAgentsLeftDrawerActiveFilters = (newValue: number) => {
    filterState.value.activeFiltersCount = newValue;
    agentsPagesPersistentData?.setFilter(filterState.value);
  };

  const clearGQL_AgentsFilter = (keys?: Array<keyof typeof GQL_AgentsFilter['value']>) => {
    if (keys) keys.forEach(key => delete GQL_AgentsFilter.value[key]);
    else {
      delete GQL_AgentsFilter.value.licenseFilter;
      delete GQL_AgentsFilter.value.coresFilter;
      delete GQL_AgentsFilter.value.availableCpuFilter;
      delete GQL_AgentsFilter.value.statusFilter;
      delete GQL_AgentsFilter.value.logLevelFilter;
      delete GQL_AgentsFilter.value.enabledAsHelperFilter;
      delete GQL_AgentsFilter.value.helperPermissionsFilter;
      delete GQL_AgentsFilter.value.buildGroupsFilter;
      delete GQL_AgentsFilter.value.cacheSizeFilter;
      delete GQL_AgentsFilter.value.agentsIdFilter;
      delete GQL_AgentsFilter.value.usedCpuFilter;
      setAgentsLeftDrawerActiveFilters(0);
      agentsPagesPersistentData?.setFilter(osService.osType.value === OsType.Windows ? defaultFilterModel.windows : defaultFilterModel.linux);
    }
  };

  const setGQL_AgentsSearchFilter = (payload: any) => {
    GQL_AgentsFilter.value.searchFilter = payload;
  };

  const setGQLFilter = (filter: string) => {
    if (osService.osType.value === OsType.Windows) {
      setGQL_AgentsFilter(filter as keyof typeof GQL_AgentsFilter.value);
    } else {
      setGQL_LinuxAgentsFilter(filter as keyof typeof GQL_AgentsFilter.value);
    }
  };
  const isLicenseFilterInDefaultState = () => {
    if (osService.osType.value === OsType.Windows) {
      const checkboxLicenseFilters = Object.keys(filterState.value.licensesFilter).every(key => {
        if (key === 'initiatorType' || key === 'ciInitiatorType' || key === 'helperType') return true;
        return (filterState.value as any).licensesFilter[key];
      });
      const initiatorTypeFilter = (filterState.value as any).licensesFilter.initiatorType.value === 'All' && (filterState.value as any).licensesFilter.initiatorType.withBuildCache === 'All';
      return checkboxLicenseFilters && initiatorTypeFilter && (filterState.value as any).licensesFilter.helperType === 'All';
    }
    return Object.keys(filterState.value.licensesFilter).every(key => (filterState.value as any).licensesFilter[key]);
  };
  const updateGQLFilterWithLocalFilterData = () => {
    Object.keys(filterState.value).forEach(key => {
      const updatedKey = osService.osType.value === OsType.Windows ? (mapFilterModelToGQLFilter.windows as any)[key] : (mapFilterModelToGQLFilter.linux as any)[key];
      if (!updatedKey) return;

      // if local filter is in default state, remove the filter from GQL request(both OS)
      switch (updatedKey) {
        case 'licenseFilter': // both os
          if (isLicenseFilterInDefaultState()) delete GQL_AgentsFilter.value.licenseFilter;
          else setGQLFilter(updatedKey);
          break;
        case 'buildGroupsFilter': // both os
          // logic handled in GQL_agentsFilterMetadata.onResult
          break;
        case 'statusFilter': // both os
          if (Object.keys(filterState.value.status).every(val => (filterState.value as any).status[val])) delete GQL_AgentsFilter.value.statusFilter;
          else setGQLFilter(updatedKey);
          break;
        case 'osVersionFilter': // windows filter
          if (osService.osType.value === OsType.Windows && Object.keys((filterState.value as any).osVersionFilter).every(val => (filterState.value as any).osVersionFilter[val]))
            delete GQL_AgentsFilter.value.osVersionFilter;
          else setGQLFilter(updatedKey);
          break;
        case 'usedCpuFilter': // linux filter
          if (osService.osType.value === OsType.Linux && filterState.value.cpuView.min === 0 && filterState.value.cpuView.max === 100) delete GQL_AgentsFilter.value.usedCpuFilter;
          else setGQLFilter(updatedKey);
          break;
        case 'logLevelFilter': // windows filter
          if (osService.osType.value === OsType.Windows && Object.keys((filterState.value as any).logLevel).every(val => (filterState.value as any).logLevel[val]))
            delete GQL_AgentsFilter.value.logLevelFilter;
          else setGQLFilter(updatedKey);
          break;
        case 'coresFilter': // windows filter
          // logic handled in GQL_agentsFilterMetadata.onResult
          break;
        case 'enabledAsHelperFilter': // windows filter
          if (osService.osType.value === OsType.Windows && (filterState.value as any).helpers.enabled && (filterState.value as any).helpers.disabled)
            delete GQL_AgentsFilter.value.enabledAsHelperFilter;
          else setGQLFilter(updatedKey);
          break;
        case 'helperPermissionsFilter': // windows filter
          if (osService.osType.value === OsType.Windows && (filterState.value as any).agentHelperPermissions.allowed && (filterState.value as any).agentHelperPermissions.notAllowed)
            delete GQL_AgentsFilter.value.helperPermissionsFilter;
          else setGQLFilter(updatedKey);
          break;
        case 'availableCpuFilter': // windows filter
          if (osService.osType.value === OsType.Windows && (filterState.value as any).cpuView.min === 0 && filterState.value.cpuView.max === 100) delete GQL_AgentsFilter.value.availableCpuFilter;
          else setGQLFilter(updatedKey);
          break;
        case 'cacheSizeFilter': // windows filter
          // logic handled in GQL_agentsFilterMetadata.onResult
          break;
        default:
          break;
      }
    });
  };

  watch(
    () => appPersistentData?.GQL?.networkStatus?.value,
    () => {
      if (agentsPagesPersistentData?.getFilter.value && containsRelevantFilters(agentsPagesPersistentData?.getFilter.value)) {
        filterState.value = agentsPagesPersistentData?.getFilter.value;
      } else {
        filterState.value = osService.osType.value === OsType.Windows ? _.cloneDeep(defaultFilterModel.windows) : _.cloneDeep(defaultFilterModel.linux);
      }

      updateGQLFilterWithLocalFilterData();
    },
    {
      immediate: true,
      deep: true,
    },
  );

  const GQL_agentsFilterMetadata = useAgentsFiltersMetadataLazyQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }), {
    pollInterval: 5000,
  });
  GQL_agentsFilterMetadata.onResult((res: any) => {
    if (res?.loading) return;
    if (osService.osType.value === OsType.Windows) {
      setAgentCacheView(res?.data?.ibManagerEntities?.agentsMetadata?.maxHelperCacheSize);
      if (
        (filterState.value as any).cacheView.min === (filterState.value as any).cacheView.limits.min &&
        (filterState.value as any).cacheView.max === (filterState.value as any).cacheView.limits.max
      ) {
        delete GQL_AgentsFilter.value.cacheSizeFilter;
      } else setGQL_AgentsFilter('cacheSizeFilter');

      setAgentsCoreView(res?.data?.ibManagerEntities?.agentsMetadata?.agentCores);
      if ((filterState.value as any).coreView.enabledFilters?.length === (filterState.value as any).coreView.existingFilters?.length) delete GQL_AgentsFilter.value.coresFilter;
      else setGQL_AgentsFilter('coresFilter');
    }

    res?.data?.ibManagerEntities?.buildGroups?.forEach((buildGroup: any) => {
      const valueToUpdate = buildGroup && Object.prototype.hasOwnProperty.call(filterState.value.buildGroups, buildGroup.name) ? (filterState.value as any).buildGroups[buildGroup.name] : true;
      updateFilter({ group: 'buildGroups', filter: buildGroup?.name, value: valueToUpdate });
    });

    if (Object.keys(filterState.value.buildGroups).every(key => (filterState.value as any).buildGroups[key])) clearGQL_AgentsFilter(['buildGroupsFilter']);
    else setGQL_AgentsFilter('buildGroupsFilter');
  });

  GQL_agentsFilterMetadata.load();

  const payload: AgentsFilter = {
    GQL_AgentsFilter: readonly(GQL_AgentsFilter),
    setGQL_AgentsFilter,
    setGQL_LinuxAgentsFilter,
    setGQL_AgentsSearchFilter,
    setGQL_AgentsQuickFilter,
    clearGQL_AgentsFilter,
    agentsLeftDrawerActiveFilters,
    setAgentsLeftDrawerActiveFilters,
    filter: filterState,
    updateFilter,
    clearFilters,
    setAgentCacheView,
    setEnabledCoreFilters,
    setAgentsCoreView,
  };
  app.provide(IKAgentsDrawerFilter, payload);
};
